import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const NewsBanner = ({ onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const newsItems = [
    {
      id: 1,
      title: "Fight for Vaquita",
      description: "A global conservation initiative dedicated to saving the world's most endangered marine mammal",
      image: "https://images.unsplash.com/photo-1584267828520-5e0d3fc22287?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
      link: "/about"
    },
    {
      id: 2, 
      title: "Critical Conservation Mission",
      description: "Working with international partners to protect the last remaining vaquitas in the Gulf of California",
      image: "https://images.unsplash.com/photo-1584269031264-b06c13643f69?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
      link: "/about"
    },
    {
      id: 3,
      title: "Research & Action",
      description: "Combining cutting-edge marine research with grassroots activism to prevent vaquita extinction",
      image: "https://images.pexels.com/photos/8824645/pexels-photo-8824645.jpeg",
      link: "/about"
    }
  ];

  // Auto-scroll functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === newsItems.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Auto-scroll every 5 seconds

    return () => clearInterval(interval);
  }, [newsItems.length]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? newsItems.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === newsItems.length - 1 ? 0 : currentIndex + 1);
  };

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Banner image clicked, navigating to:', newsItems[currentIndex].link);
    if (onImageClick) {
      onImageClick(newsItems[currentIndex].link);
    }
  };

  return (
    <div className="relative bg-gray-800 border-t border-b border-gray-700 overflow-hidden">
      <div className="relative h-40 sm:h-48 md:h-56 lg:h-64">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out"
          style={{
            backgroundImage: `url('${newsItems[currentIndex].image}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/40"></div>
        </div>

        {/* Clickable overlay */}
        <div 
          className="absolute inset-0 cursor-pointer z-10"
          onClick={handleImageClick}
        >
        </div>

        {/* Content */}
        <div className="absolute inset-0 flex items-center z-20 pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl">
              <div className="flex items-center space-x-3 mb-3">
                <div className="h-1 w-8 bg-blue-500"></div>
                <span className="text-blue-300 text-xs sm:text-sm font-semibold uppercase tracking-wider">
                  Conservation Initiative
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 leading-tight">
                {newsItems[currentIndex].title}
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed">
                {newsItems[currentIndex].description}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200 z-30"
          aria-label="Previous news item"
        >
          <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200 z-30"
          aria-label="Next news item"
        >
          <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>

        {/* Indicators */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-30">
          {newsItems.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 w-2 rounded-full transition-all duration-200 z-30 ${
                index === currentIndex ? 'bg-white' : 'bg-white/40'
              }`}
              aria-label={`Go to news item ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 h-1 bg-blue-500 transition-all duration-300 ease-linear"
           style={{ width: `${((currentIndex + 1) / newsItems.length) * 100}%` }}>
      </div>
    </div>
  );
};

export default NewsBanner;